import {
  FREQUENTLY_OF_SYMPTOMS_OPTIONS,
  PATIENT_SYMPTOMS_OPTIONS,
  USED_HEART_MONITOR_OPTIONS,
} from '@/shared/config';
import { CustomButton, CustomSelect, RadioGroup } from '@/shared/ui/components';
import { t } from 'ttag';
import { useMedicalDrawer } from './useMedicalDrawer';
import type { MedicalDrawerProps } from './MedicalDrawer.types';
import './style.scss';

/**
 * Renders the patient medical history drawer with a multi-select of experienced symptoms bound to the useMedicalDrawer state. The symptom frequency select and the prior heart monitor radio group are only rendered once at least one symptom is selected, and cancel/save reflect the saving flag.
 *
 * @param data - Existing medical history values used to seed the form.
 * @param handleClick - Callback closing the drawer, also wired to the cancel button.
 * @param isSaving - Whether a save request is in flight, disabling cancel and loading save.
 * @returns The medical history editing form with action buttons.
 */
const MedicalDrawer = ({ data, handleClick, isSaving }: MedicalDrawerProps) => {
  const { state, onChange, onClickBottomButton } = useMedicalDrawer({ data, handleClick });

  return (
    <div className="list-input">
      <CustomSelect
        mode="multiple"
        title={t`Symptoms the patient experienced`}
        aria-label={t`Symptoms the patient experienced`}
        showSearch
        allowClear={false}
        name="patientSymptoms"
        size="middle"
        placeholder={t`No symptom`}
        options={PATIENT_SYMPTOMS_OPTIONS}
        value={state.patientSymptoms}
        onChange={onChange}
      />
      {state.patientSymptoms?.length > 0 && (
        <>
          <CustomSelect
            title={t`How many times did the symptoms happen?`}
            aria-label={t`How many times did the symptoms happen?`}
            showSearch
            allowClear={false}
            name="frequentlyOfSymptoms"
            size="middle"
            placeholder={t`Select a range`}
            options={FREQUENTLY_OF_SYMPTOMS_OPTIONS}
            value={state.frequentlyOfSymptoms}
            onChange={onChange}
          />
          <div>
            <span>{t`Had a heart monitor before?`}</span>
            <div className="flex mt2">
              <RadioGroup
                className="radio-input"
                value={state.usedHeartMonitor}
                name="usedHeartMonitor"
                onChange={onChange}
                options={USED_HEART_MONITOR_OPTIONS as any}
                size="small"
              />
            </div>
          </div>
        </>
      )}
      <div className="bottom-button-row">
        <CustomButton
          id="cancel-btn"
          style="neutral"
          type="outline"
          onClick={handleClick}
          styles={{ minWidth: 80 }}
          disabled={isSaving}
        >
          {t`Cancel`}
        </CustomButton>
        <CustomButton
          id="save-btn"
          onClick={onClickBottomButton}
          styles={{ minWidth: 80 }}
          loading={isSaving}
        >
          {t`Save`}
        </CustomButton>
      </div>
    </div>
  );
};

export default MedicalDrawer;
